import { urlFor } from "@/utils/client";
import { IPartner, IStat, IStats } from "@/utils/types";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import Marquee from "react-fast-marquee";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
});

function parseValue(value: string) {
  const match = value.match(/^([^\d]*?)([\d,.]+)(.*)$/);
  if (!match) return { prefix: "", number: 0, suffix: value, hasNumber: false };
  return {
    prefix: match[1],
    number: parseFloat(match[2].replace(/,/g, "")),
    suffix: match[3],
    hasNumber: true,
  };
}

function formatNumber(n: number, target: number) {
  const decimals = target.toString().includes(".")
    ? target.toString().split(".")[1].length
    : 0;
  const formatted = n.toFixed(decimals);
  if (target >= 1000 && !target.toString().includes(",")) return formatted;
  if (target.toString().includes(",")) {
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return formatted;
}

function useCountUp(value: string) {
  const { prefix, number, suffix, hasNumber } = parseValue(value);
  const [display, setDisplay] = useState(
    hasNumber ? `${prefix}0${suffix}` : value
  );
  const hasAnimated = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  const animate = useCallback(() => {
    if (hasAnimated.current || !hasNumber) return;
    hasAnimated.current = true;

    const duration = 1600;
    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * number;
      setDisplay(`${prefix}${formatNumber(current, number)}${suffix}`);
      if (progress < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }, [hasNumber, number, prefix, suffix]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  return { ref, display };
}

function StatValue({ item, small }: { item: IStat; small?: boolean }) {
  const { ref, display } = useCountUp(item.value);

  return (
    <div ref={ref} className="flex flex-col gap-1">
      <p
        className={`font-display ${
          small ? "text-3xl" : "text-4xl md:text-5xl"
        }`}
      >
        {display}
      </p>
      <p className="text-base opacity-80">{item.label}</p>
      {item.button?.link && item.button?.label && (
        <p className="font-display mt-1">
          {item.button.label} <span aria-hidden>&rarr;</span>
        </p>
      )}
    </div>
  );
}

function shuffle(array: IPartner[]): IPartner[] {
  let currentIndex = array.length,
    randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

function LogoMarquees({ partners }: { partners: IPartner[] }) {
  // Initialize unshuffled and shuffle in an effect to avoid hydration mismatches.
  const [rows, setRows] = useState<IPartner[][]>([
    partners,
    partners,
    partners,
  ]);

  useEffect(() => {
    if (partners.length === 0) return;
    setRows([
      shuffle([...partners]),
      shuffle([...partners]),
      shuffle([...partners]),
    ]);
  }, [partners]);

  return (
    <div className="flex flex-col gap-6">
      {rows.map((row, idx) => (
        <Marquee
          key={idx}
          speed={25}
          direction={idx % 2 === 1 ? "right" : "left"}
          gradient
          gradientColor="#FFFFFF"
          autoFill
        >
          {row.map((partner, i) => {
            if (!partner.logo?.image) return null;
            return (
              <Image
                key={i}
                className="h-10 w-auto max-w-[140px] object-contain mr-10 invert opacity-50"
                width={280}
                height={80}
                alt={partner.logo.caption ?? ""}
                src={urlFor(partner.logo.image).url()}
              />
            );
          })}
        </Marquee>
      ))}
    </div>
  );
}

// Repeating 8-slot bento pattern on an 8-column grid where one row unit is
// half a card: a wide marquee slot (3/8), a horizontally centered card (2/8,
// sitting in the middle of the page), two stacked half-height cards (3/8),
// then four equal full-height cards.
function bentoClasses(i: number) {
  switch (i % 8) {
    case 0:
      return "sm:col-span-2 lg:col-span-3 lg:row-span-2";
    case 1:
      return "lg:col-span-2 lg:row-span-2";
    case 2:
    case 3:
      return "lg:col-span-3";
    default:
      return "lg:col-span-2 lg:row-span-2";
  }
}

function StatCard({
  item,
  partners,
  index,
  bento,
}: {
  item: IStat;
  partners?: IPartner[];
  index: number;
  bento: boolean;
}) {
  const small = bento && (index % 8 === 2 || index % 8 === 3);
  const showLogos = !!item.showLogos && !!partners?.length;
  const media = showLogos ? undefined : item.media?.[0];
  const image = media?._type === "iwc" && media.image ? media : undefined;
  const video = media?._type === "vwc" && media.playbackId ? media : undefined;

  const icon = item.icon?.image ? (
    <Image
      width={48}
      height={48}
      alt={item.icon.caption || ""}
      src={urlFor(item.icon.image).url()}
      className="object-contain w-12 h-12 invert"
    />
  ) : null;

  let content;
  if (showLogos) {
    content = (
      <div className="flex h-full min-h-[280px] lg:min-h-0 flex-col justify-between gap-6 py-6 md:py-8">
        <LogoMarquees partners={partners!} />
        <div className="px-6 md:px-8">
          <StatValue item={item} small={small} />
        </div>
      </div>
    );
  } else if (image || video) {
    content = (
      // Below lg the card is content-sized; on lg the bento row (170px for the
      // two small slots) defines the height, so the overlay must not force it.
      <div className="relative h-full min-h-[240px] lg:min-h-0">
        {image && (
          <Image
            fill
            sizes="(max-width: 1024px) 100vw, 25vw"
            className="object-cover"
            src={urlFor(image.image).url()}
            alt={image.caption ?? ""}
          />
        )}
        {video && (
          <MuxPlayer
            streamType="on-demand"
            playbackId={video.playbackId}
            autoPlay
            muted
            loop
            playsInline
            thumbnailTime={0.1}
            disableCookies
            className="absolute inset-0 w-full h-full"
            style={
              {
                ["--media-object-fit" as never]: "cover",
                ["--controls" as never]: "none",
              } as CSSProperties
            }
            title={video.caption ?? ""}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div
          className={`absolute inset-x-0 bottom-0 text-white ${
            small ? "p-4 md:p-5" : "p-6 md:p-8"
          }`}
        >
          <StatValue item={item} small={small} />
        </div>
      </div>
    );
  } else {
    content = (
      <div
        className={`flex h-full p-6 md:p-8 gap-4 ${
          small
            ? "flex-row items-center justify-between"
            : "flex-col justify-between min-h-[200px]"
        }`}
      >
        {small ? (
          <>
            <StatValue item={item} small />
            {icon}
          </>
        ) : (
          <>
            {icon}
            <StatValue item={item} />
          </>
        )}
      </div>
    );
  }

  const classes = `block h-full bg-white text-darkGrey overflow-hidden ${
    bento ? bentoClasses(index) : ""
  } ${
    item.button?.link
      ? "transition-transform hover:scale-[1.02] active:scale-100"
      : ""
  }`;

  if (item.button?.link) {
    if (item.button.link.startsWith("http")) {
      return (
        <a
          href={item.button.link}
          target="_blank"
          rel="noopener noreferrer"
          className={classes}
        >
          {content}
        </a>
      );
    }
    return (
      <Link href={item.button.link} className={classes}>
        {content}
      </Link>
    );
  }

  return <div className={classes}>{content}</div>;
}

export function Stats(props: IStats) {
  const items = props.items?.filter(Boolean) ?? [];
  const bento = items.length >= 4;

  return (
    <div
      style={{
        backgroundColor: props.accentColor || undefined,
        color: props.backgroundColor || undefined,
      }}
      className={`${
        props.invert ? "bg-grey text-darkGrey" : "bg-darkGrey text-white"
      } ${
        props.noBg ? "!bg-grey pb-0" : "py-16"
      } px-4 flex flex-col component-gap`}
    >
      <div className="flex flex-col component-gap text-center max-w-5xl m-auto">
        <p className="heading">{props.title}</p>
        {props.description && <p className="opacity-80">{props.description}</p>}
      </div>
      {items.length > 0 && (
        <div
          className={`max-w-page m-auto w-full grid gap-4 md:gap-6 ${
            bento
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 lg:auto-rows-[170px] lg:grid-flow-dense"
              : `grid-cols-1 sm:grid-cols-2 ${
                  items.length >= 3 ? "lg:grid-cols-3" : ""
                }`
          }`}
        >
          {items.map((item, i) => (
            <StatCard
              key={i}
              item={item}
              partners={props.partners}
              index={i}
              bento={bento}
            />
          ))}
        </div>
      )}
    </div>
  );
}
